import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import { SupportCategoryItem } from '@/api/placementApi.schemas';
import Slider from '@/components/Slider/Slider';
import { WalletCardArbitrage } from '@/components/WalletCardArbitrage/WalletCardArbitrage';
import { BREAKPOINTS } from '@/constants/breakpoints';
import useScreenSize from '@/hooks/useScreenSize';

import styles from './styles.module.css';

interface SummaryWalletArbitrageProps {
  exitFees: number;
  entryFees: number;
  beforeCategories: SupportCategoryItem[] | null | undefined;
  afterCategories: SupportCategoryItem[] | null | undefined;
}

const SummaryWalletArbitrage = ({
  exitFees,
  entryFees,
  beforeCategories,
  afterCategories,
}: SummaryWalletArbitrageProps) => {
  const { t } = useTranslation();
  const { windowWidth } = useScreenSize();
  const isMobile = windowWidth < BREAKPOINTS.mobile;

  const before = beforeCategories ?? [];
  const after = afterCategories ?? [];

  // Ces donuts n'ont de sens que par comparaison. Si l'API n'a pas pu calculer l'une des deux
  // répartitions, on n'en affiche aucune : une moitié de comparaison, sans rien qui signale
  // l'absence de l'autre, se lirait comme la répartition complète.
  const canCompare = before.length > 0 && after.length > 0;

  const walletCards = canCompare
    ? [
        { id: 'before', title: t('allocation.before'), categories: before },
        { id: 'after', title: t('allocation.after'), categories: after },
      ]
    : [];

  const walletSlides = walletCards.map(({ id, title, categories }) => ({
    id,
    content: <WalletCardArbitrage title={title} categories={categories} />,
  }));

  return (
    <div className={styles.summaryWalletArbitrageContainer}>
      {canCompare && (
        <>
          <h2 className={styles.arbitrageSummaryTitle}>{t('walletArbitrage.title')}</h2>
          {isMobile ? (
            <Slider
              slides={walletSlides}
              slidesPerView="auto"
              spaceBetween={0}
              freeMode={true}
              showNavigation={false}
              showPlayPause={false}
              className={styles.walletSlider}
              initialSlide={1}
              height="auto"
            />
          ) : (
            <MtxGrid row>
              {walletCards.map(({ id, title, categories }) => (
                <WalletCardArbitrage key={id} title={title} categories={categories} />
              ))}
            </MtxGrid>
          )}
        </>
      )}
      <div className={styles.feesContainer}>
        <p className={styles.fees}>
          {t('walletArbitrage.exitFees')}
          <span className={styles.feesValue}> {t('format.currencyFlexible', { value: exitFees })}</span>
        </p>
        <p className={styles.fees}>
          {t('walletArbitrage.entryFees')}
          <span className={styles.feesValue}> {t('format.currencyFlexible', { value: entryFees })}</span>
        </p>
        <p className={styles.fees}>
          {t('walletArbitrage.totalFees')}
          <span className={styles.feesValue}> {t('format.currencyFlexible', { value: exitFees + entryFees })}</span>
        </p>
      </div>
    </div>
  );
};

export { SummaryWalletArbitrage };
